export function etaFor(variant, quantity) {
  if (variant?.state !== 'incoming' || !Number.isSafeInteger(quantity) || quantity <= 0) return null;
  let remaining = quantity;
  for (const arrival of variant.arrivals ?? []) {
    if (!Number.isSafeInteger(arrival.quantity) || arrival.quantity < 0 || !/^\d{4}-\d{2}-\d{2}$/.test(arrival.eta)) return null;
    remaining -= arrival.quantity;
    if (remaining <= 0) return arrival.eta;
  }
  return null;
}

export function currentVariant(snapshot, id, now = Date.now()) {
  const age = now - Date.parse(snapshot?.updatedAt);
  if (snapshot?.enabled !== true || !Number.isFinite(age) || age < 0 || age > 180000) return null;
  return snapshot.variants?.find(v => v.variantId === 'gid://shopify/ProductVariant/' + id) ?? null;
}

export function checkoutItems(quote, copy) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(quote?.eta) || !Array.isArray(quote.items) || !quote.items.length) throw Error('Invalid pre-sale quote');
  let total = 0;
  const items = quote.items.map(item => {
    if (!/^\d+$/.test(String(item.id)) || !Number.isSafeInteger(item.quantity) || item.quantity <= 0 || !item.properties?._hm_presale_quote) throw Error('Invalid pre-sale item');
    total += item.quantity;
    return { ...item, properties: { ...item.properties, [copy.etaLabel]: quote.eta, [copy.togetherLabel]: copy.together } };
  });
  if (total !== quote.quantity) throw Error('Pre-sale quantity mismatch');
  return items;
}

if (typeof document !== 'undefined') {
  const root = () => window.Shopify?.routes?.root || '/';
  const forms = new Set();
  async function request(path, options = {}) {
    const response = await fetch(root() + path, { credentials: 'same-origin', cache: 'no-store', ...options });
    if (!response.ok) throw Error('Pre-sale unavailable');
    return response.json();
  }
  function attach(box) {
    if (box._presale) return;
    const form = box.closest('form'); const container = box.closest('[data-product-form]');
    if (!form || !container) return;
    const copy = JSON.parse(box.querySelector('[data-presale-copy]').textContent);
    const types = JSON.parse(box.querySelector('[data-presale-types]').textContent);
    const button = box.querySelector('[data-presale-add]'); const message = box.querySelector('[data-presale-message]');
    const state = { snapshot: null, busy: false, override: null }; box._presale = state; forms.add(box);
    const selection = () => ({ id: String(new FormData(form).get('id') || ''), quantity: Number(new FormData(form).get('quantity') || 1) });
    const signature = v => v.id + ':' + v.quantity;
    function paint() {
      const selected = selection(); const variant = currentVariant(state.snapshot, selected.id);
      const mode = variant?.state === 'ready' ? 'ready' : variant?.state === 'incoming' ? 'incoming' : 'blocked';
      container.dataset.hmPresaleMode = mode;
      box.style.visibility = mode === 'ready' ? 'hidden' : '';
      const eta = state.override?.signature === signature(selected) ? state.override.eta : etaFor(variant, selected.quantity);
      box.dataset.eta = eta || '';
      message.textContent = mode === 'incoming' && eta ? copy.eta.replace('[date]', eta) + ' ' + copy.together : copy.unavailable;
      button.disabled = state.busy || mode !== 'incoming' || !eta;
      const section = container.closest('.shopify-section');
      section?.querySelectorAll('[data-delivery-estimate]').forEach(el => {
        el.dataset.dePresaleState = mode;
        el.dataset.dePresaleEta = eta || '';
        if (window.HillmalayaDeliveryEstimate?.refresh) window.HillmalayaDeliveryEstimate.refresh(el);
        else el.style.visibility = mode === 'ready' ? '' : 'hidden';
      });
    }
    box._paint = paint;
    box._refresh = async () => {
      try { state.snapshot = await request('apps/hillmalaya-presale/presale/state?productId=' + encodeURIComponent(box.dataset.productId)); }
      catch { state.snapshot = null; }
      state.override = null; paint();
    };
    form.addEventListener('change', () => { state.override = null; paint(); });
    form.addEventListener('input', event => { if (event.target.name === 'quantity') { state.override = null; paint(); } });
    form.addEventListener('click', event => {
      if (event.target.closest('[data-update-quantity]')) queueMicrotask(() => { state.override = null; paint(); });
    });
    button.addEventListener('click', async () => {
      if (state.busy) return;
      const selected = selection(); const expectedETA = box.dataset.eta;
      if (!expectedETA || !currentVariant(state.snapshot, selected.id)) { await box._refresh(); return; }
      state.busy = true; paint();
      try {
        const properties = {};
        for (const [key, value] of new FormData(form)) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        if (!properties['Order Type 訂單類別']?.includes('Final Sale')) properties['Order Type 訂單類別'] = types[selected.id];
        const quote = await request('apps/hillmalaya-presale/presale/quote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ variantId: selected.id, quantity: selected.quantity, properties }) });
        if (signature(selection()) !== signature(selected)) return;
        if (quote.eta !== expectedETA) {
          state.override = { signature: signature(selected), eta: quote.eta }; paint(); message.textContent = copy.changed + ' ' + message.textContent; return;
        }
        const items = checkoutItems(quote, copy);
        await request('cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        window.location.assign(root() + 'cart');
      } catch {
        await box._refresh(); message.textContent = copy.failed;
      } finally { state.busy = false; button.disabled = !box.dataset.eta; }
    });
    paint(); box._refresh();
  }
  function scan(node) {
    if (node.nodeType !== 1 && node !== document) return;
    if (node.matches?.('[data-presale]')) attach(node);
    node.querySelectorAll('[data-presale]').forEach(attach);
  }
  function stopDirect(event) {
    const form = event.target.closest('form'); const container = form?.closest('[data-hm-presale-mode]');
    if (!container || container.dataset.hmPresaleMode === 'ready') return;
    if (event.type === 'submit' || event.target.closest('[data-add-to-cart-trigger]')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }
  document.addEventListener('click', stopDirect, true);
  document.addEventListener('submit', stopDirect, true);
  function init() {
    scan(document);
    new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'attributes') record.target.closest('form')?.querySelector('[data-presale]')?._paint();
        else for (const node of record.addedNodes) scan(node);
      }
      for (const box of forms) if (!box.isConnected) forms.delete(box);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['variant'] });
    setInterval(() => { for (const box of forms) if (!box._presale.busy) box._refresh(); }, 60000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
}
